import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/data/site";
import { ShipmentMap } from "@/components/dashboard/ShipmentMap";
import { Package, MapPin, CalendarBlank, CheckCircle, Circle, Boat, SignOut, ChartLineUp, Plus } from "@phosphor-icons/react";

function StatusPill({ status }) {
  const map = { "Delivered": "bg-teal/15 text-teal-dark", "In Transit": "bg-blue-100 text-brand-blue", "QC Passed": "bg-amber-100 text-amber-700" };
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${map[status] || "bg-slate-100 text-slate-600"}`}>{status}</span>;
}

export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/client/shipments").then((r) => setShipments(r.data)).catch(() => {});
    api.get("/client/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-mist" data-testid="client-dashboard">
      <div className="bg-ink">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 lg:px-8">
          <Link to="/" className="flex items-center gap-3">
            <div className="overflow-hidden rounded-md bg-[#EEF2FB] p-1"><img src={BRAND.logo} alt="logo" className="h-8 w-8 object-contain" /></div>
            <span className="font-heading text-base font-bold text-white">Client Portal</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-slate-300 sm:inline">{user?.name}</span>
            <button onClick={logout} data-testid="client-logout" className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"><SignOut size={18} /> Logout</button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-ink">Welcome back, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="mt-1 text-slate-500">Track your active shipments and sourcing projects.</p>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            { label: "Active Shipments", value: shipments.filter((s) => s.status !== "Delivered").length, icon: Boat },
            { label: "Sourcing Projects", value: projects.length, icon: ChartLineUp },
            { label: "Completed", value: shipments.filter((s) => s.status === "Delivered").length, icon: CheckCircle },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-gradient-to-br from-[#1E73BE] to-[#16B894] text-white"><s.icon size={24} weight="duotone" /></div>
              <div><p className="font-heading text-2xl font-bold text-ink">{s.value}</p><p className="text-xs text-slate-500">{s.label}</p></div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold text-ink">Your Shipments</h2>
          <Link to="/contact?type=quote" className="flex items-center gap-1.5 text-sm font-semibold text-teal hover:underline"><Plus size={16} weight="bold" /> New shipment</Link>
        </div>
        <div className="mt-5">
          <ShipmentMap shipments={shipments} />
        </div>
        <div className="mt-5 space-y-5">
          {shipments.map((s, i) => (
            <div key={s.id} data-testid={`shipment-${i}`} className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading text-lg font-semibold text-ink">{s.title}</h3>
                    <StatusPill status={s.status} />
                  </div>
                  <p className="mt-1 font-mono text-xs text-slate-400">{s.tracking} · {s.mode}</p>
                </div>
                <div className="text-right text-xs text-slate-500">
                  <p className="flex items-center gap-1.5"><MapPin size={14} className="text-teal" /> {s.origin} → {s.destination}</p>
                  <p className="mt-1 flex items-center gap-1.5"><CalendarBlank size={14} className="text-teal" /> ETA {s.eta}</p>
                </div>
              </div>

              <div className="mt-5">
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#1E73BE] to-[#16B894]" style={{ width: `${s.progress}%` }} />
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                {s.milestones.map((m, idx) => {
                  const completed = (idx / (s.milestones.length - 1)) * 100 <= s.progress;
                  return (
                    <span key={m} className={`flex items-center gap-1.5 text-xs ${completed ? "text-ink font-medium" : "text-slate-400"}`}>
                      {completed ? <CheckCircle size={15} weight="fill" className="text-teal" /> : <Circle size={15} />} {m}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
          {shipments.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400">No shipments yet.</p>}
        </div>

        <h2 className="mt-12 font-heading text-xl font-semibold text-ink">Sourcing Projects</h2>
        <div className="mt-5 grid gap-5 md:grid-cols-3">
          {projects.map((p, i) => (
            <div key={p.id} data-testid={`project-${i}`} className="rounded-xl border border-slate-200 bg-white p-6">
              <span className="rounded-full bg-mist px-3 py-1 text-xs font-semibold text-ink">{p.stage}</span>
              <h3 className="mt-3 font-heading text-base font-semibold text-ink">{p.name}</h3>
              <p className="mt-1 text-xs text-slate-500">{p.category}</p>
              <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-gradient-to-r from-[#1E73BE] to-[#16B894]" style={{ width: `${p.progress}%` }} />
              </div>
              <p className="mt-3 text-xs text-slate-400">{p.supplier}</p>
              <p className="mt-1 text-xs text-slate-400">Updated {p.updated}</p>
            </div>
          ))}
          {projects.length === 0 && <p className="md:col-span-3 rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400">No projects yet.</p>}
        </div>
      </div>
    </div>
  );
}
